import { useEffect, useState } from 'react'

import { useSession } from '../auth/useSession'
import { CopyButton } from '../components/CopyButton'
import { Dialog } from '../components/Dialog'
import { Alert } from '../components/States'
import type { TotpEnrollment } from '../lib/apiTypes'
import { describeError } from '../lib/describeError'
import { useConfirmTotp, useDisableTotp, useEnrollTotp } from '../lib/hooks'

export function SecurityPage({ mandatory = false }: { mandatory?: boolean }) {
  const { user, logout, totpRequired } = useSession()
  const [editing, setEditing] = useState(false)
  const [disabling, setDisabling] = useState(false)
  const [feedback, setFeedback] = useState('')
  const [logoutError, setLogoutError] = useState<unknown>(null)
  const [leaving, setLeaving] = useState(false)

  return (
    <>
      <div className="page-head"><div className="page-head__text"><h1>{mandatory ? 'Aktifkan autentikator untuk melanjutkan' : 'Keamanan akun'}</h1><p className="muted">{mandatory ? 'Kebijakan admin mewajibkan verifikasi dua langkah. Data pembayaran belum dapat dibuka sampai kode pertama terverifikasi.' : `Verifikasi dua langkah untuk ${user?.email ?? ''}.`}</p></div></div>
      {feedback ? <Alert tone="success">{feedback}</Alert> : null}
      {logoutError ? <Alert tone="danger">{describeError(logoutError)}</Alert> : null}
      {mandatory ? <EnrollmentForm onDone={() => setFeedback('Autentikator aktif.')} /> : (
        <section className="card">
          <h2>Autentikator TOTP</h2>
          <p>{user?.totp_enabled ? 'Autentikator aktif. Kode diperlukan setiap kali masuk.' : 'Autentikator belum aktif. Gunakan aplikasi autentikator yang mendukung TOTP.'}</p>
          {totpRequired ? <p className="muted">Kebijakan admin mewajibkan autentikator; penonaktifan tidak tersedia.</p> : null}
          <div className="state__actions">
            {!user?.totp_enabled ? <button className="btn btn--primary" type="button" onClick={() => setEditing(true)}>Aktifkan autentikator</button> : !totpRequired ? <button className="btn btn--danger" type="button" onClick={() => setDisabling(true)}>Nonaktifkan autentikator</button> : null}
          </div>
        </section>
      )}
      {mandatory ? <button type="button" className="btn" disabled={leaving} onClick={() => { setLeaving(true); setLogoutError(null); void logout().catch(setLogoutError).finally(() => setLeaving(false)) }}>{leaving ? 'Keluar...' : 'Keluar'}</button> : null}
      {editing ? <Dialog title="Aktifkan autentikator" onClose={() => setEditing(false)}><EnrollmentForm onDone={() => { setEditing(false); setFeedback('Autentikator aktif. Sesi lain sudah dicabut.') }} /></Dialog> : null}
      {disabling ? <DisableDialog onClose={() => setDisabling(false)} onDone={() => { setDisabling(false); setFeedback('Autentikator dinonaktifkan. Sesi lain sudah dicabut.') }} /> : null}
    </>
  )
}

function EnrollmentForm({ onDone }: { onDone: () => void }) {
  const { signIn } = useSession()
  const enroll = useEnrollTotp()
  const confirm = useConfirmTotp()
  const [password, setPassword] = useState('')
  const [code, setCode] = useState('')
  const [enrollment, setEnrollment] = useState<TotpEnrollment | null>(null)
  const [error, setError] = useState<unknown>(null)
  const busy = enroll.isPending || confirm.isPending
  useEffect(() => {
    if (!enrollment) return
    const timer = window.setTimeout(() => { setEnrollment(null); setCode(''); setError(new Error('Pengaturan autentikator kedaluwarsa. Buat secret baru dengan password saat ini.')) }, 10 * 60 * 1000)
    return () => window.clearTimeout(timer)
  }, [enrollment])

  return (
    <form className="dialog__body" onSubmit={(event) => {
      event.preventDefault()
      setError(null)
      if (enrollment) {
        confirm.mutate(code, {
          onSuccess: (session) => { setEnrollment(null); setCode(''); confirm.reset(); signIn(session); onDone() },
          onError: (cause) => { setError(cause); setCode(''); confirm.reset() },
        })
      } else {
        enroll.mutate(password, {
          onSuccess: (result) => { setEnrollment(result); setPassword(''); enroll.reset() },
          onError: (cause) => { setError(cause); setPassword(''); enroll.reset() },
        })
      }
    }}>
      {error ? <Alert tone="danger">{describeError(error)}</Alert> : null}
      {enrollment ? (
        <>
          <Alert tone="warning">Secret hanya ditampilkan selama pengaturan ini terbuka. Tambahkan ke aplikasi autentikator sebelum menutup. Pengaturan berlaku 10 menit; setelah kedaluwarsa, mulai ulang.</Alert>
          <div className="field"><span className="field__label">Secret autentikator</span><code className="secret-value mono">{enrollment.secret}</code><CopyButton value={enrollment.secret} label="Salin secret autentikator" /></div>
          <div className="field"><span className="field__label">URI autentikator</span><code className="secret-value mono">{enrollment.otpauth_uri}</code><CopyButton value={enrollment.otpauth_uri} label="Salin URI autentikator" /></div>
          <div className="field"><label className="field__label" htmlFor="confirm-totp-code">Kode pertama dari autentikator</label><input id="confirm-totp-code" className="input" inputMode="numeric" autoComplete="one-time-code" maxLength={6} value={code} onChange={(event) => setCode(event.target.value.replace(/\D/g, ''))} /></div>
          <button type="button" className="btn" disabled={busy} onClick={() => { setEnrollment(null); setCode(''); setError(null); confirm.reset() }}>Mulai ulang pengaturan</button>
        </>
      ) : <div className="field"><label className="field__label" htmlFor="enroll-password">Password saat ini</label><input id="enroll-password" className="input" type="password" autoComplete="current-password" value={password} onChange={(event) => setPassword(event.target.value)} /></div>}
      <button className="btn btn--primary" type="submit" disabled={busy || (enrollment ? code.length !== 6 : !password)}>{busy ? 'Memeriksa...' : enrollment ? 'Verifikasi dan aktifkan' : 'Buat secret autentikator'}</button>
    </form>
  )
}

function DisableDialog({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const { reload } = useSession()
  const disable = useDisableTotp()
  const [password, setPassword] = useState('')
  const [code, setCode] = useState('')
  const [error, setError] = useState<unknown>(null)
  return <Dialog title="Nonaktifkan autentikator" onClose={onClose}>
    <form className="dialog__body" onSubmit={(event) => {
      event.preventDefault(); setError(null)
      disable.mutate({ password, code }, {
        onSuccess: () => { setPassword(''); setCode(''); disable.reset(); onDone(); reload() },
        onError: (cause) => { setPassword(''); setCode(''); setError(cause); disable.reset() },
      })
    }}>
      <Alert tone="warning">Penonaktifan mencabut sesi lain. Kebijakan admin dapat menolak aksi ini.</Alert>
      {error ? <Alert tone="danger">{describeError(error)}</Alert> : null}
      <div className="field"><label className="field__label" htmlFor="disable-password">Password saat ini</label><input id="disable-password" className="input" type="password" autoComplete="current-password" value={password} onChange={(event) => setPassword(event.target.value)} /></div>
      <div className="field"><label className="field__label" htmlFor="disable-totp-code">Kode autentikator</label><input id="disable-totp-code" className="input" inputMode="numeric" autoComplete="one-time-code" maxLength={6} value={code} onChange={(event) => setCode(event.target.value.replace(/\D/g, ''))} /></div>
      <button className="btn btn--danger" type="submit" disabled={disable.isPending || !password || code.length !== 6}>{disable.isPending ? 'Memeriksa...' : 'Nonaktifkan autentikator'}</button>
    </form>
  </Dialog>
}
